/**
 * Invoice contract (RBAC area: `invoices`).
 *
 * Invoices are generated *on demand* from one of four sources (see `INVOICE_SOURCES`).
 * A run previews the invoices that would be created as of a *Thru Date*, the operator
 * selects which to create, and each is stamped with an *Invoice Date*. Provenance is
 * recorded on `source` plus `agreementId`/`projectId`.
 *
 * Lifecycle: `draft → in_review → sent → void`, with `closed` reachable from `draft` or
 * `in_review` for invoices that are finalized without sending. Draft and in-review
 * invoices are editable or deletable; once `sent` an invoice is immutable and can only be
 * voided; `closed` and `void` are terminal. Monetary and hour fields are decimals
 * serialized as strings end-to-end — never JS floats (see docs/conventions.md).
 */
export const INVOICE_STATUSES = [
    'draft',
    'in_review',
    'on_hold',
    'sent',
    'closed',
    'void',
];
/**
 * Whether the document is a regular invoice (customer owes) or a credit memo (customer is
 * credited). Both share the same model, lifecycle, and INV- numbering; a credit memo
 * stores positive amounts and is labeled + treated as a credit. Credit memos are not
 * pushed to QuickBooks as invoices.
 */
export const INVOICE_DOCUMENT_TYPES = ['invoice', 'credit_memo'];
export const INVOICE_DOCUMENT_TYPE_LABELS = {
    invoice: 'Invoice',
    credit_memo: 'Credit memo',
};
/** The source mode an invoice was generated from. */
export const INVOICE_SOURCES = [
    'product_subscription',
    'managed_services',
    'project',
    'tickets',
    'manual',
];
/** The four on-demand generation modes (excludes `manual`). */
export const INVOICE_GENERATION_SOURCES = [
    'product_subscription',
    'managed_services',
    'project',
    'tickets',
];
/** Category of an invoice line. */
export const INVOICE_LINE_KINDS = ['subscription', 'overage', 'time', 'fee'];
/** Statuses in which an invoice's content may be edited or the invoice deleted. */
export const INVOICE_EDITABLE_STATUSES = [
    'draft',
    'in_review',
    'on_hold',
];
/**
 * Allowed status transitions. Keys are the current status; values are the set of
 * statuses reachable from it. A `sent` or `closed` invoice can be reopened to `draft`
 * (the operator removes it from QuickBooks by hand). `on_hold` parks an invoice: it
 * stays editable but has NO transition to `sent`, so it can neither be emailed nor
 * pushed to QuickBooks until the hold is released. `void` is terminal.
 */
export const INVOICE_TRANSITIONS = {
    draft: ['in_review', 'on_hold', 'sent', 'closed'],
    in_review: ['draft', 'on_hold', 'sent', 'closed'],
    on_hold: ['draft', 'in_review', 'closed'],
    sent: ['void', 'draft'],
    closed: ['draft'],
    void: [],
};
/** True when an invoice may move from `from` to `to`. */
export function canTransitionInvoice(from, to) {
    return INVOICE_TRANSITIONS[from].includes(to);
}
/** True when an invoice in `status` may have its content edited or be deleted. */
export function isInvoiceEditable(status) {
    return INVOICE_EDITABLE_STATUSES.includes(status);
}
/** Human-readable invoice number prefix. */
export const INVOICE_NUMBER_PREFIX = 'INV-';
/** Zero-padding width for the numeric portion of an invoice number. */
export const INVOICE_NUMBER_PAD = 6;
/** Formats an internal sequence into a display invoice number, e.g. 42 → "INV-000042". */
export function formatInvoiceNumber(seq) {
    return `${INVOICE_NUMBER_PREFIX}${String(seq).padStart(INVOICE_NUMBER_PAD, '0')}`;
}
/** Default payment terms (days from issue date to due date). */
export const INVOICE_DEFAULT_NET_TERMS_DAYS = 30;
/**
 * Maps a Net Terms label (e.g. "Net 30", "Due on receipt") to the number of days from
 * the invoice date to the due date. "Due on receipt" is 0 days; a value containing a
 * number uses that number; anything unrecognized falls back to the default.
 */
export function netTermsToDays(terms) {
    if (!terms)
        return INVOICE_DEFAULT_NET_TERMS_DAYS;
    if (/due on receipt/i.test(terms))
        return 0;
    const match = /(\d+)/.exec(terms);
    return match ? Number(match[1]) : INVOICE_DEFAULT_NET_TERMS_DAYS;
}
/** Default invoice currency (ISO 4217). */
export const INVOICE_DEFAULT_CURRENCY = 'USD';
//# sourceMappingURL=invoice.js.map